/**
 * Avatar — initials-in-a-coloured-circle. The app's first shared avatar
 * component (initials were previously inlined everywhere). Colour is stable
 * per person: pass an explicit `index` to cycle the palette, otherwise a
 * stable hash of userId/name picks a colour so the same person is always the
 * same colour across surfaces.
 */

import { AVATAR_COLORS, getAvatarColor } from '@/data/map-config';

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZES: Record<AvatarSize, { box: string; text: string }> = {
  xs: { box: 'w-5 h-5', text: 'text-[9px]' },
  sm: { box: 'w-6 h-6', text: 'text-[10px]' },
  md: { box: 'w-8 h-8', text: 'text-xs' },
  lg: { box: 'w-10 h-10', text: 'text-sm' },
};

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0; // keep 32-bit
  }
  return Math.abs(h);
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]!.toUpperCase();
  return (parts[0][0]! + parts[parts.length - 1][0]!).toUpperCase();
}

interface AvatarProps {
  name: string;
  /** Explicit palette index (cycled). Omit to derive a stable colour from
   *  userId/name. */
  index?: number;
  userId?: string;
  size?: AvatarSize;
  /** Show a surface-coloured ring (useful in overlapping stacks). */
  ring?: boolean;
  className?: string;
  title?: string;
}

export function Avatar({
  name,
  index,
  userId,
  size = 'md',
  ring = false,
  className = '',
  title,
}: AvatarProps) {
  const s = SIZES[size];
  const color =
    index != null
      ? getAvatarColor(index)
      : AVATAR_COLORS[hashString(userId || name) % AVATAR_COLORS.length];

  return (
    <div
      className={`${s.box} ${s.text} rounded-full flex items-center justify-center font-bold text-white shrink-0 ${
        ring ? 'ring-2 ring-surface-card' : ''
      } ${className}`}
      style={{ backgroundColor: color }}
      title={title ?? name}
      aria-label={name}
    >
      {initials(name)}
    </div>
  );
}
